import { createConsumer } from '@rails/actioncable';

// CRITICAL: Reduced from 60s to 30s for more frequent presence updates
// This ensures faster detection of connection status changes on mobile and desktop
// Mobile browsers may suspend timers, but 30s provides a good balance
const PRESENCE_INTERVAL = 30000;
const RECONNECT_INTERVAL = 1000;
const VISIBILITY_BUFFER_MS = 2000;
const DISCONNECT_CHECK_INTERVAL_MS = 400;
const DISCONNECT_MAX_WAIT_MS = 5000;

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(app, pubsubToken, websocketHost = '') {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.isDisposed = false;
    this.consumer = createConsumer(websocketURL);
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        disconnected: () => {
          if (this.isDisposed) return;
          const wasDisconnected = BaseActionCableConnector.isDisconnected;
          BaseActionCableConnector.isDisconnected = true;
          this.disconnectTimestamp = Date.now();
          if (!wasDisconnected) {
            this.onConnectionLost();
          }
          this.queueDisconnectNotification({ reset: true });
          this.initReconnectTimer();
        },
      }
    );
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.presenceTimer = null;
    this.pendingDisconnectCheck = null;
    this.disconnectNotified = false;
    // A mobile browser can reconnect the socket while this document is
    // backgrounded. Defer data recovery until it is foregrounded instead of
    // losing the reconnect signal along with the intentionally hidden banner.
    this.pendingReconnectRecovery = false;
    this.visibilityHandlers = {};
    this.visibilityState = {
      isBackgrounded: this.isPageActuallyHidden(),
      lastHiddenAt: this.isPageActuallyHidden() ? Date.now() : 0,
      lastVisibleAt: this.isPageActuallyHidden() ? 0 : Date.now(),
    };
    this.isAValidEvent = () => true;
    
    // Setup visibility change listener for mobile presence handling
    this.setupVisibilityListener();
    
    // Setup Service Worker message listener for background sync
    this.setupServiceWorkerListener();
    
    // Start presence updates
    this.triggerPresenceInterval();
  }

  setupServiceWorkerListener = () => {
    // CRITICAL: Listen for messages from Service Worker
    // This allows background sync to trigger presence updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener(
        'message',
        this.handleServiceWorkerMessage
      );
      
      // Register presence sync with Service Worker
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'REGISTER_PRESENCE_SYNC',
        });
      }
    }
  };

  handleServiceWorkerMessage = event => {
    if (
      !this.isDisposed &&
      event.data?.type === 'SYNC_PRESENCE' &&
      this.consumer?.connection?.isOpen()
    ) {
      this.sendPresenceUpdate();
    }
  };

  setupVisibilityListener = () => {
    this.visibilityHandlers = {
      visibility: this.handleVisibilityChange,
      pagehide: this.handlePageHide,
      pageshow: this.handlePageShow,
      blur: this.handleWindowBlur,
      focus: this.handleWindowFocus,
    };

    document.addEventListener(
      'visibilitychange',
      this.visibilityHandlers.visibility,
      { passive: true }
    );
    window.addEventListener(
      'pagehide',
      this.visibilityHandlers.pagehide,
      { passive: true }
    );
    window.addEventListener(
      'pageshow',
      this.visibilityHandlers.pageshow,
      { passive: true }
    );
    window.addEventListener(
      'blur',
      this.visibilityHandlers.blur,
      { passive: true }
    );
    window.addEventListener(
      'focus',
      this.visibilityHandlers.focus,
      { passive: true }
    );

    if (typeof document.addEventListener === 'function') {
      this.visibilityHandlers.freeze = this.handleDocumentFreeze;
      this.visibilityHandlers.resume = this.handleDocumentResume;
      document.addEventListener(
        'freeze',
        this.visibilityHandlers.freeze,
        { passive: true }
      );
      document.addEventListener(
        'resume',
        this.visibilityHandlers.resume,
        { passive: true }
      );
    }
  };

  sendPresenceUpdate = () => {
    // Only send if we have an active subscription
    if (this.subscription && this.subscription.updatePresence) {
      this.subscription.updatePresence();
    }
  };

  triggerPresenceInterval = () => {
    // Clear any existing timer
    if (this.presenceTimer) {
      clearTimeout(this.presenceTimer);
    }

    this.presenceTimer = setTimeout(() => {
      // Only send presence if:
      // 1. Page is visible (not minimized/locked)
      // 2. Connection is active (WebSocket is open)
      // This prevents updating Redis timestamp when user is actually offline
      const isConnectionActive = this.consumer?.connection?.isOpen();
      
      if (this.isPageCurrentlyActive() && isConnectionActive) {
        this.sendPresenceUpdate();
      }
      this.triggerPresenceInterval();
    }, PRESENCE_INTERVAL);
  };

  checkConnection() {
    if (this.isDisposed) return;
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      // Do not fetch dashboard data while a mobile browser is backgrounded;
      // timers and requests may be suspended. Remember the real reconnect and
      // perform the recovery as soon as the document becomes active.
      if (this.isPageCurrentlyActive()) {
        this.onReconnect();
      } else {
        this.pendingReconnectRecovery = true;
      }
      this.disconnectNotified = false;
      BaseActionCableConnector.isDisconnected = false;
    } else {
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  // This is intentionally separate from `onDisconnected`. The latter is
  // delayed or suppressed while a mobile app is backgrounded to avoid a
  // misleading banner, but recovery still needs the original timestamp.
  // eslint-disable-next-line class-methods-use-this
  onConnectionLost = () => {};

  disconnect() {
    this.isDisposed = true;
    // Clean up timers
    if (this.presenceTimer) {
      clearTimeout(this.presenceTimer);
      this.presenceTimer = null;
    }
    this.clearReconnectTimer();
    if (this.pendingDisconnectCheck) {
      clearTimeout(this.pendingDisconnectCheck);
      this.pendingDisconnectCheck = null;
    }
    this.teardownVisibilityListener();
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener(
        'message',
        this.handleServiceWorkerMessage
      );
    }
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isDisposed) return;
    try {
      if (!this.isAValidEvent(data)) {
        return;
      }

      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`[ActionCable] Failed to handle event ${event}`, error);
    }
  };

  isPageActuallyHidden = () => {
    if (typeof document === 'undefined') {
      return false;
    }
    if (typeof document.visibilityState === 'string') {
      return document.visibilityState !== 'visible';
    }
    if (typeof document.hidden === 'boolean') {
      return document.hidden;
    }
    return false;
  };

  isPageCurrentlyActive = () => {
    if (typeof document === 'undefined') {
      return true;
    }
    if (this.isPageActuallyHidden()) {
      return false;
    }
    return !this.visibilityState?.isBackgrounded;
  };

  markHidden = () => {
    if (!this.visibilityState) {
      return;
    }
    this.visibilityState.isBackgrounded = true;
    this.visibilityState.lastHiddenAt = Date.now();
  };

  markVisible = () => {
    if (!this.visibilityState) {
      return;
    }
    this.visibilityState.isBackgrounded = false;
    this.visibilityState.lastVisibleAt = Date.now();
  };

  wasHiddenRecently = (threshold = VISIBILITY_BUFFER_MS) => {
    if (!this.visibilityState || !this.visibilityState.lastHiddenAt) {
      return false;
    }
    return Date.now() - this.visibilityState.lastHiddenAt < threshold;
  };

  shouldDelayDisconnectNotice = () => {
    if (this.isPageActuallyHidden()) {
      return true;
    }
    if (this.visibilityState?.isBackgrounded) {
      return true;
    }
    if (this.wasHiddenRecently()) {
      return true;
    }
    return false;
  };

  queueDisconnectNotification = ({ reset = false } = {}) => {
    if (!BaseActionCableConnector.isDisconnected) {
      if (this.pendingDisconnectCheck) {
        clearTimeout(this.pendingDisconnectCheck);
        this.pendingDisconnectCheck = null;
      }
      return;
    }

    if (reset) {
      this.disconnectNotified = false;
    }

    if (this.disconnectNotified) {
      if (this.pendingDisconnectCheck) {
        clearTimeout(this.pendingDisconnectCheck);
        this.pendingDisconnectCheck = null;
      }
      return;
    }

    if (this.pendingDisconnectCheck) {
      clearTimeout(this.pendingDisconnectCheck);
      this.pendingDisconnectCheck = null;
    }

    const startTime = Date.now();

    const evaluate = () => {
      if (!BaseActionCableConnector.isDisconnected) {
        this.pendingDisconnectCheck = null;
        return;
      }

      if (this.disconnectNotified) {
        this.pendingDisconnectCheck = null;
        return;
      }

      if (this.shouldDelayDisconnectNotice()) {
        if (Date.now() - startTime < DISCONNECT_MAX_WAIT_MS) {
          this.pendingDisconnectCheck = setTimeout(
            evaluate,
            DISCONNECT_CHECK_INTERVAL_MS
          );
        } else {
          this.pendingDisconnectCheck = null;
        }
        return;
      }

      this.pendingDisconnectCheck = null;
      this.disconnectNotified = true;
      this.onDisconnected();
    };

    this.pendingDisconnectCheck = setTimeout(
      evaluate,
      DISCONNECT_CHECK_INTERVAL_MS
    );
  };

  handleVisibilityChange = () => {
    if (this.isPageActuallyHidden()) {
      this.markHidden();
      return;
    }

    this.markVisible();

    if (BaseActionCableConnector.isDisconnected) {
      this.queueDisconnectNotification();
      this.checkConnection();
    }

    this.runPendingReconnectRecovery();

    const isConnectionActive = this.consumer?.connection?.isOpen();
    if (isConnectionActive) {
      this.sendPresenceUpdate();
    }
  };

  handlePageHide = () => {
    this.markHidden();
  };

  handlePageShow = () => {
    this.markVisible();
    if (BaseActionCableConnector.isDisconnected) {
      this.queueDisconnectNotification();
      this.checkConnection();
    }
    this.runPendingReconnectRecovery();
  };

  handleWindowBlur = () => {
    this.markHidden();
  };

  handleWindowFocus = () => {
    this.markVisible();
    if (BaseActionCableConnector.isDisconnected) {
      this.queueDisconnectNotification();
      this.checkConnection();
    }
    this.runPendingReconnectRecovery();
  };

  handleDocumentFreeze = () => {
    this.markHidden();
  };

  handleDocumentResume = () => {
    this.markVisible();
    if (BaseActionCableConnector.isDisconnected) {
      this.queueDisconnectNotification();
      this.checkConnection();
    }
    this.runPendingReconnectRecovery();
  };

  runPendingReconnectRecovery = () => {
    if (!this.pendingReconnectRecovery || !this.isPageCurrentlyActive()) {
      return;
    }

    this.pendingReconnectRecovery = false;
    this.onReconnect();
  };

  teardownVisibilityListener = () => {
    if (!this.visibilityHandlers) {
      return;
    }

    const {
      visibility,
      pagehide,
      pageshow,
      blur,
      focus,
      freeze,
      resume,
    } = this.visibilityHandlers;

    if (visibility) {
      document.removeEventListener('visibilitychange', visibility);
    }
    if (pagehide) {
      window.removeEventListener('pagehide', pagehide);
    }
    if (pageshow) {
      window.removeEventListener('pageshow', pageshow);
    }
    if (blur) {
      window.removeEventListener('blur', blur);
    }
    if (focus) {
      window.removeEventListener('focus', focus);
    }
    if (freeze) {
      document.removeEventListener('freeze', freeze);
    }
    if (resume) {
      document.removeEventListener('resume', resume);
    }

    this.visibilityHandlers = {};
  };
}

export default BaseActionCableConnector;
